export class Slider {
    constructor(viewer, axis, sliderId, orientation, updateAddedVolume = false, inverted = false) {
        this.viewer = viewer;
        this.axis = axis;
        this.inverted = inverted;
        this.sliderId = sliderId;
        this.orientation = orientation;
        this.element = null;
        this.isLocked = false;
        this.minDistanceBetweenHandles = 1.5;
        this.size = 0;
        this.distance = 0;
        this.updateAddedVolume = updateAddedVolume;
        this.minValue = 0;
        this.maxValue = 0;

        this.viewer.scene.addEventListener('volume_added', this.onVolumeAdded.bind(this));
    }

    onVolumeAdded(event) {
        const minValue = this.minValue;
        const maxValue = this.maxValue;

        if (this.updateAddedVolume) {
            this.updateVolume(event.volume, minValue, maxValue);

            const onVolumeDropped = () => {
                this.updateVolume(event.volume, minValue, maxValue);
                event.volume.removeEventListener('drop', onVolumeDropped);
            }

            event.volume.addEventListener('drop', onVolumeDropped);
        }
    }

    init(size) {
        this.element = $(`#${this.sliderId}`);
        this.size = size[this.axis];

        this.element.slider({
            range: true,
            values: [ 0, this.size ],
            min: 0,
            max: this.size,
            step: 0.001,
            orientation: this.orientation,
            slide: (event, ui) => this.handleSliderUpdate(event, ui),
            stop: () => this.viewer.transformationTool.resetFrameColor()
        });

        this.minValue = 0;
        this.maxValue = this.size;

        this.element.on('mouseleave', (e) => {
            this.hideHandleValue();
        })

        this.element.bind('wheel', (e) => {
            const event = e.originalEvent;
            const {clientX, clientY, ctrlKey, shiftKey} = event;
            const deltaWheel = event.deltaY || event.detail || -event.wheelDelta;

            const {left, top, height, width} = this.element[0].getBoundingClientRect();

            const ratioX = (clientX - left) / width;
            const ratioY = (clientY - top) / height;
            const ratio = this.orientation === "vertical" ? ratioY : ratioX;

            const sceneBoundingBox = this.viewer.scene.calculatedBoundingBox;

            const getStepSize = () => {
                const defaultStepSize = sceneBoundingBox ? sceneBoundingBox.size[this.axis] / 200 : 1;
                const ctrlFactor = 3;
                const shiftFactor = 0.1;

                if (shiftKey) {
                    return this.orientation === "horizontal" ? defaultStepSize * shiftFactor : 0.04;
                }
                if (ctrlKey) {
                    return defaultStepSize * ctrlFactor;
                }
                return defaultStepSize;
            }

            let delta = getStepSize();

            const values = this.element.slider('values');
            let maxValue = values[1];
            let minValue = values[0];
            let value = 0;
            let handleIndex = 0;

            if (deltaWheel < 0) {
                delta *= -1;
            }

            const ratioMin = minValue / this.size;
            const ratioMax = maxValue / this.size;
            const ratioToUse = this.orientation === 'vertical' ? 1 - ratio : ratio;
            const isCloserToMin = Math.abs(ratioMin - ratioToUse) <= Math.abs(ratioMax - ratioToUse);

            if (isCloserToMin) {
                minValue -= delta;

                if (minValue <= 0) {
                    minValue = 0;
                } else if (minValue > maxValue) {
                    minValue = maxValue;
                }

                value = minValue;

                if (this.isLocked) {
                    maxValue = minValue + this.distance;
                }
            } else {
                maxValue -= delta;

                if (maxValue < minValue) {
                    maxValue = minValue;
                }

                value = maxValue;
                handleIndex = 1;

                if (this.isLocked) {
                    minValue = maxValue - this.distance;
                }
            }

            this.minValue = minValue;
            this.maxValue = maxValue;
            const result = this.element.slider('option', 'slide').call(this.element, e, { values: [minValue, maxValue], value: value });
            if (result !== false) {
                this.element.slider('values', [minValue, maxValue], 'value', value);
                this.showHandleValue(value, handleIndex);
            }
            return false;
        })
    }

    updateSliderValues(volume) {
        const {minValue, maxValue} = this.processVolumeValues(volume);
        this.minValue = minValue;
        this.maxValue = maxValue;
        this.element.slider({values: [minValue, maxValue]});
        this.setDistance();
    }

    handleSliderUpdate(event, sliderValues) {
        const {minValue, maxValue, isOutOfBounds} = this.processSliderValues(sliderValues);
        this.minValue = minValue;
        this.maxValue = maxValue;

        if (isOutOfBounds) {
            event.preventDefault();
            return;
        }

        this.viewer.transformationTool.updateFrameColor(this.axis);

        this.updateSelectedVolume(minValue, maxValue);

        if (this.isLocked) {
            this.updateLockedSlider(minValue, maxValue);
        }
    }

    updateSliderAccessibility(isSelected) {
        this.element.slider(!isSelected ? "disable" : "enable");
    }

    updateSelectedVolume(minValue, maxValue) {
        let selectedVolume = this.viewer.scene.getSelectedVolume();

        if (!selectedVolume) {
            selectedVolume = this.viewer.scene.defaultClipVolume;
        }

        this.updateVolume(selectedVolume, minValue, maxValue)
    }

    updateVolume(volume, minValue, maxValue) {
        volume.position[this.axis] = this.getVolumePosition(minValue, maxValue);

        if (!this.isLocked) {
            volume.scale[this.axis] = maxValue - minValue;
        }
    }

    updateLockedSlider(minValue, maxValue) {
        this.element.slider('values', [minValue, maxValue]);
    }

    handleSliderLock() {
        this.setIsLocked(!this.isLocked);
        this.setDistance();
        this.updateSliderLockButton();
    }

    getVolumePosition(minValue, maxValue) {
        const {minBoundingBoxPosition, maxBoundingBoxPosition} = this.getBoundingBoxPosition();

        let min = minValue;
        let max = maxValue;

        if (this.orientation === 'vertical' || this.inverted) {
            return maxBoundingBoxPosition + (min + (max - min) / 2);
        } else {
            return minBoundingBoxPosition - (min + (max - min) / 2);
        }
    }

    processVolumeValues(volume) {
        const {boundingBoxSize, minBoundingBoxPosition, maxBoundingBoxPosition} = this.getBoundingBoxPosition();

        const minVolumePosition = volume.position[this.axis] + volume.scale[this.axis] / 2;
        const maxVolumePosition = volume.position[this.axis] - volume.scale[this.axis] / 2;

        let minValue = minBoundingBoxPosition - minVolumePosition;
        let maxValue = boundingBoxSize - (maxVolumePosition - maxBoundingBoxPosition);

        if (this.orientation === 'vertical') {
            minValue = maxVolumePosition - maxBoundingBoxPosition;
            maxValue = boundingBoxSize - (minBoundingBoxPosition - minVolumePosition);
        }

        return {minValue, maxValue}
    }

    processSliderValues(sliderValues) {
        const {value, values, handleIndex} = sliderValues;

        let minValue = values[0];

        let maxValue = values[1];
        let isOutOfBounds = false;

        if (!this.isLocked && maxValue - minValue < this.minDistanceBetweenHandles) {
            return {minValue, maxValue, isOutOfBounds: true}
        }

        if (this.isLocked) {
            const valueDiff = maxValue - minValue;
            const maxSize = parseFloat(this.element.slider('option', 'max').toFixed(2));
            // Use a threshold here since Slider Widgets maxSliderHandle doesn't always reach max entirely
            const maxSizeThreshold = 0.1;

            if (handleIndex === 0) {
                if ((maxValue + maxSizeThreshold >= maxSize) && valueDiff <= this.distance) {
                    isOutOfBounds = true;
                }

                maxValue = value + this.distance;
            }

            if (handleIndex === 1) {
                if (minValue === 0 && valueDiff <= this.distance) {
                    isOutOfBounds = true;
                }

                minValue = value - this.distance;
            }

            this.showHandleValue(minValue, 0);
            this.showHandleValue(maxValue, 1);
        } else {
            this.showHandleValue(value, handleIndex);
        }

        return {minValue, maxValue, isOutOfBounds}
    }

    updateSliderLockButton() {
        const lockButtonElement = this.element.parent().find('.slider-lock-button');
        if (this.isLocked) {
            lockButtonElement.addClass('slider-lock-button-active');
        } else {
            lockButtonElement.removeClass('slider-lock-button-active');
        }
    }

    showHandleValue(value, handleIndex) {
        if (this.sliderId !== 'sldHeight') return;

        const handleElement = this.element.find('.ui-slider-handle').eq(handleIndex);
        if (!handleElement) return;

        let clearedValue = value;

        if (value > this.element.slider('option', 'max')) {
            clearedValue = this.element.slider('option', 'max');
        }

        if (value < 0) {
            clearedValue = 0;
        }

        if (this.isLocked) {
            const maxDiff = this.element.slider('option', 'max') - this.distance;
            const minDiff = this.element.slider('option', 'min') + this.distance;

            if (handleIndex === 0 && value >= maxDiff) {
                clearedValue = maxDiff;
            }

            if (handleIndex === 1 && value <= minDiff) {
                clearedValue = minDiff;
            }
        }

        const displayValue = `${clearedValue.toFixed(2)} m`
        const valueElement = handleElement.find($('.slider-handle-value')).eq(0);

        if (valueElement && valueElement.length > 0) {
            valueElement.children().text(displayValue)
        } else {
            handleElement.append(`
                <div class="slider-handle-value slider-ui-background">
                    <span>${displayValue}</span>
                </div>
            `);
        }
    }

    hideHandleValue() {
        if (this.sliderId !== 'sldHeight') return;

        const handleElements = this.element.find('.slider-handle-value');

        if (!handleElements || handleElements.length === 0) return;

        handleElements.each(function () {
            $(this).remove();
        });
    }

    setIsLocked(isLocked) {
        this.isLocked = isLocked;
    }

    setDistance() {
        const values = this.element.slider('values');
        this.distance = values[1] - values[0];
    }

    getBoundingBoxPosition() {
        const boundingBoxPosition = this.viewer.scene.calculatedBoundingBox.position[this.axis];
        const boundingBoxSize = this.viewer.scene.calculatedBoundingBox.size[this.axis];

        const minBoundingBoxPosition = boundingBoxPosition + boundingBoxSize / 2;
        const maxBoundingBoxPosition = boundingBoxPosition - boundingBoxSize / 2;

        return {boundingBoxSize, minBoundingBoxPosition, maxBoundingBoxPosition}
    }
}
